import { useState, useEffect, useCallback } from "react";
import { getPageContentBySlug } from "@/actions";
import { bioCoatingImg, safGuideImg, SustainableFuleImg } from "@/assets/images";

export const parseText = (content) => {
  if (!content) return "";
  if (typeof content === "object") {
    if (content.blocks && Array.isArray(content.blocks)) {
      return content.blocks.map((b) => b.text).filter(Boolean).join("\n");
    }
  }
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      if (parsed?.blocks && Array.isArray(parsed.blocks)) {
        return parsed.blocks.map((b) => b.text).filter(Boolean).join("\n");
      }
    } catch (e) {}
    return content.replace(/<[^>]*>/g, "").trim();
  }
  return String(content);
};

export const parseJsonOrArray = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
  }
  return null;
};

export const getImageUrl = (src, imageUrlBase) => {
  if (!src) return "";
  if (typeof src !== "string") return src;
  if (
    src.startsWith("http://") ||
    src.startsWith("https://") ||
    src.startsWith("data:")
  ) {
    return src;
  }
  if (imageUrlBase) {
    const cleanBase = imageUrlBase.endsWith("/")
      ? imageUrlBase.slice(0, -1)
      : imageUrlBase;
    const cleanPath = src.startsWith("/") ? src : `/${src}`;
    return `${cleanBase}${cleanPath}`;
  }
  return src;
};

export const isVideoUrl = (url, isVideoFlag = false) => {
  if (!url) return false;
  if (typeof url !== "string") return false;

  const cleanUrl = url.split("?")[0].split("#")[0].toLowerCase();
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v", ".mkv", ".avi"];
  const imageExtensions = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".svg", ".avif", ".bmp", ".ico"];

  if (videoExtensions.some((ext) => cleanUrl.endsWith(ext))) {
    return true;
  }
  if (imageExtensions.some((ext) => cleanUrl.endsWith(ext))) {
    return false;
  }

  return Boolean(isVideoFlag);
};

const useOurTechnology = (initialPageData = null, slug = "our-technology") => {
  const [data, setData] = useState(initialPageData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [livePreviewData, setLivePreviewData] = useState(null);

  const fetchPageContent = useCallback(
    async (targetSlug = slug) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getPageContentBySlug(targetSlug);
        if (res?.success && res?.data) {
          setData(res.data);
          return res.data;
        }
      } catch (err) {
        console.error(`Error fetching technology page content for (${targetSlug}):`, err);
        setError(err?.message || "Failed to fetch technology page content");
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  useEffect(() => {
    if (!initialPageData) {
      fetchPageContent(slug);
    } else {
      setData(initialPageData);
    }
  }, [initialPageData, slug, fetchPageContent]);

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (event.data?.type === "SUSTANIX_PREVIEW_DATA") {
        setLivePreviewData(event.data.data);
      }
    };
    window.addEventListener("message", handlePreviewMessage);
    return () => window.removeEventListener("message", handlePreviewMessage);
  }, []);

  const pageContent = data?.page?.page_content || {};
  const imageUrlBase = data?.imageUrl || "";

  const heroPhotoRaw =
    livePreviewData?.heroCoverPhoto ||
    pageContent.heroCoverPhoto ||
    pageContent.cover_photo ||
    pageContent.heroPhoto ||
    pageContent.image;
  const heroPhoto = getImageUrl(heroPhotoRaw, imageUrlBase);

  const section3PhotoRaw =
    livePreviewData?.section3Photo ||
    pageContent.section3Photo ||
    pageContent.section3_photo;
  const section3Photo = getImageUrl(section3PhotoRaw, imageUrlBase) || bioCoatingImg;

  const section5PhotoRaw =
    livePreviewData?.section5Photo ||
    pageContent.section5Photo ||
    pageContent.section5_photo;
  const section5Photo = getImageUrl(section5PhotoRaw, imageUrlBase);

  const rawCards =
    parseJsonOrArray(livePreviewData?.section3Cards) ||
    parseJsonOrArray(pageContent.section3Cards || pageContent.section3_cards);

  const formattedCards = Array.isArray(rawCards)
    ? rawCards.map((item, index) => {
        const cardSrc = getImageUrl(item.icon || item.src || item.photo, imageUrlBase);
        const isVideo = isVideoUrl(cardSrc, item.isVideo || item.is_video);
        return {
          ...item,
          id: item.id || index + 1,
          title: parseText(item.title || item.text),
          text: parseText(item.text || item.title),
          cardSrc,
          isVideo,
        };
      })
    : null;

  const rawFaqs =
    parseJsonOrArray(livePreviewData?.section6Faqs) ||
    parseJsonOrArray(pageContent.section6Faqs || pageContent.section6_faqs);

  const formattedFaqs = Array.isArray(rawFaqs)
    ? rawFaqs.map((faq, index) => ({
        id: faq.id || index + 1,
        question: parseText(
          faq.question || faq.q || faq.title || `Question ${index + 1}`
        ),
        answer: parseText(faq.answer || faq.a || faq.description || ""),
      }))
    : null;

  const activeContent = {
    // Hero
    heroTitle:
      livePreviewData?.heroTitle ||
      parseText(pageContent.heroTitle || pageContent.title) ||
      "Barrier coatings for paper",
    heroDescription:
      livePreviewData?.heroDescription ||
      parseText(pageContent.heroDescription || pageContent.description) ||
      "High-performance Water, Grease & Water Vapour barrier for paper via a single-layer coating; engineered from agricultural biomass and seamlessly applied using existing coating machine lines.",
    heroCoverPhoto: heroPhoto,
    heroIsVideo: isVideoUrl(
      heroPhotoRaw,
      livePreviewData?.heroIsVideo ??
        (pageContent.heroIsVideo || pageContent.is_video || false)
    ),

    // Section 2
    section2Title:
      livePreviewData?.section2Title ||
      parseText(pageContent.section2Title || pageContent.section2_title),
    section2Description:
      livePreviewData?.section2Description ||
      parseText(
        pageContent.section2Description || pageContent.section2_description
      ) ||
      "Delivering high barrier performance to paper with proven recyclability, food-contact safety, and metal-free, PFAS-free, zero-microplastic technology; engineered to reduce grammage, ensure PPWR compliance, and optimize ultra-low grammage flexible packaging.",
    section2DrawerTitle:
      livePreviewData?.section2DrawerTitle ||
      parseText(pageContent.section2DrawerTitle || pageContent.section2_drawer_title),
    section2DrawerDescription:
      livePreviewData?.section2DrawerDescription ||
      parseText(pageContent.section2DrawerDescription || pageContent.section2_drawer_description),
    section2Photo:
      getImageUrl(
        livePreviewData?.section2Photo ||
          pageContent.section2Photo ||
          pageContent.section2_photo,
        imageUrlBase
      ) || SustainableFuleImg,
    section2CallActions:
      parseJsonOrArray(livePreviewData?.section2CallActions) ||
      parseJsonOrArray(
        pageContent.section2CallActions || pageContent.section2_call_actions
      ),

    // Section 3
    section3Title:
      livePreviewData?.section3Title ||
      parseText(pageContent.section3Title || pageContent.section3_title) ||
      "Shunya™ barrier bio coating:",
    section3Description:
      livePreviewData?.section3Description ||
      parseText(
        pageContent.section3Description || pageContent.section3_description
      ),
    section3Photo: section3Photo,
    section3IsVideo: isVideoUrl(
      section3PhotoRaw,
      livePreviewData?.section3IsVideo ??
        (pageContent.section3IsVideo || pageContent.section3_is_video || false)
    ),
    section3Cards: formattedCards,

    // Section 4
    section4Title:
      livePreviewData?.section4Title ||
      parseText(pageContent.section4Title || pageContent.section4_title),
    section4Description:
      livePreviewData?.section4Description ||
      parseText(
        pageContent.section4Description || pageContent.section4_description
      ) ||
      "In a water-dispersed coating, evaporation of water requires huge amounts of power up-to 3.3 GWh per assembly line per year. Btw, 65% of gross weight of the coating is water.",
    section4DrawerTitle:
      livePreviewData?.section4DrawerTitle ||
      parseText(pageContent.section4DrawerTitle || pageContent.section4_drawer_title),
    section4DrawerDescription:
      livePreviewData?.section4DrawerDescription ||
      parseText(pageContent.section4DrawerDescription || pageContent.section4_drawer_description),
    section4Photo:
      getImageUrl(
        livePreviewData?.section4Photo ||
          pageContent.section4Photo ||
          pageContent.section4_photo,
        imageUrlBase
      ) || safGuideImg,
    section4CallActions:
      parseJsonOrArray(livePreviewData?.section4CallActions) ||
      parseJsonOrArray(
        pageContent.section4CallActions || pageContent.section4_call_actions
      ),

    // Section 5
    section5Title:
      livePreviewData?.section5Title ||
      parseText(pageContent.section5Title || pageContent.section5_title) ||
      "-83%",
    section5Description:
      livePreviewData?.section5Description ||
      parseText(
        pageContent.section5Description || pageContent.section5_description
      ) ||
      "CO2 emissions (Scope 1 & 3) & water saved with Sustanix’s 100% bio-based solid coating system compared to other water-based formulations",
    section5DrawerTitle:
      livePreviewData?.section5DrawerTitle ||
      parseText(pageContent.section5DrawerTitle || pageContent.section5_drawer_title),
    section5DrawerDescription:
      livePreviewData?.section5DrawerDescription ||
      parseText(pageContent.section5DrawerDescription || pageContent.section5_drawer_description),
    section5Photo: section5Photo,
    section5IsVideo: isVideoUrl(
      section5PhotoRaw,
      livePreviewData?.section5IsVideo ??
        (pageContent.section5IsVideo || pageContent.section5_is_video || false)
    ),
    section5CallActions:
      parseJsonOrArray(livePreviewData?.section5CallActions) ||
      parseJsonOrArray(
        pageContent.section5CallActions || pageContent.section5_call_actions
      ),

    // Section 6
    section6Title:
      livePreviewData?.section6Title ||
      parseText(pageContent.section6Title || pageContent.section6_title),
    section6Faqs: formattedFaqs,
  };

  return {
    data,
    setData,
    loading,
    error,
    livePreviewData,
    activeContent,
    imageUrlBase,
    fetchPageContent,
    getImageUrl: (src) => getImageUrl(src, imageUrlBase),
    isVideoUrl,
    parseText,
    parseJsonOrArray,
  };
};

export default useOurTechnology;
